/**
 * stream-plane.ts — PRI-496 Lane B streaming cutover kill-switch.
 *
 * Selects which streaming plane the web app rides:
 *
 *   'public' (DEFAULT) — the published Streams API: POST /v1/streams
 *                        (options.narrative + recording) + the versioned
 *                        /v1/streams/{id}/signal WS. This is the dogfooding
 *                        plane — the SPA uses the same contract a customer's
 *                        agent uses.
 *   'legacy'           — the internal /v1/stream/signal WS plane (pre-cutover).
 *                        Kept operational as the instant-rollback path.
 *
 * Rollback is INSTANT and requires no deploy:
 *   localStorage.setItem('primate:stream-plane', 'legacy')  // per-browser
 * or set VITE_STREAM_PLANE=legacy at build time (env rollback for all users).
 *
 * Precedence: localStorage > env > default.
 *
 * NOTE (tests): vitest runs (MODE === 'test') default to 'legacy' because the
 * existing unit suites mock the legacy SignallingClient protocol; the public
 * adapter has its own dedicated suite (public-stream-signalling.test.ts).
 * Playwright dev servers pin VITE_STREAM_PLANE=legacy in playwright.config.ts
 * for the same reason — the public-plane e2e spec overrides via localStorage.
 */

export type StreamPlane = 'public' | 'legacy'

export const STREAM_PLANE_STORAGE_KEY = 'primate:stream-plane'

function isPlane(v: unknown): v is StreamPlane {
  return v === 'public' || v === 'legacy'
}

/** Resolve the active streaming plane. Read at session start (not module load)
 *  so flipping the localStorage kill-switch applies to the NEXT stream. */
export function resolveStreamPlane(): StreamPlane {
  // 1. localStorage kill-switch — instant per-browser rollback.
  try {
    const stored = localStorage.getItem(STREAM_PLANE_STORAGE_KEY)
    if (isPlane(stored)) return stored
  } catch { /* SSR / storage disabled */ }
  // 2. Build-time env override.
  const env = (import.meta.env.VITE_STREAM_PLANE as string | undefined) ?? ''
  if (isPlane(env)) return env
  // 3. vitest default: legacy (unit suites mock the legacy protocol).
  if (import.meta.env.MODE === 'test') return 'legacy'
  // 4. Production default: the public plane.
  return 'public'
}
